import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import { InfiniteQueryResult, QueryResult } from "react-query";
import { AxiosError } from "axios";

import NavArrowIcon from "components/NavArrowIcon";
import { IconButton } from "fe-shared/src/components";

import { ApiError } from "fe-shared/src/types/common";
import { NavigationInvoices } from "routes/InvoicesDetails/modules/api";
import { getColor } from "fe-shared/src/helpers/colors";

import styles from "./InvoiceNavigation.module.scss";

import * as api from "routes/InvoicesDetails/modules/api";

interface Props {
  navigationInvoices: InfiniteQueryResult<
    NavigationInvoices,
    AxiosError<ApiError>
  >;
  invoiceListInfo: QueryResult<api.InvoiceListInfo, AxiosError<ApiError>>
  isDirectnavigation: boolean;

}

const InvoiceNavigation = ({ navigationInvoices, invoiceListInfo, isDirectnavigation: isDirectnNavigation }: Props) => {
  const {
    data: invoiceListInfoResult,
    isLoading: invoiceListInfoResultInProgress
  } = invoiceListInfo;
  const {
    data,
    canFetchMore,
    fetchMore,
    isFetchingMore,
  } = navigationInvoices;

  const history = useHistory();
  const { t } = useTranslation();
  const { path } = useRouteMatch();
  const { invoiceNumber } = useParams<{ invoiceNumber: string }>();
  const total = data?.[0]?.total || 0;
  const invoiceNumbers = useMemo(
    () =>
      (data || []).reduce(
        (result, page) => [
          ...result,
          ...page.invoicesIds.map((i) => i.invoiceNumber),
        ],
        [] as string[]
      ),
    [data]
  );
  const currentInvoiceIndex = useMemo(
    () => invoiceNumbers.indexOf(invoiceNumber),
    [invoiceNumber, invoiceNumbers]
  );

  const getInvoiceUrl = (index: number) =>
    path.replace(
      ":invoiceNumber",
      `${invoiceNumbers[index]}?navigation=true`
    );

  const isFetchMore = canFetchMore &&
    !isFetchingMore &&
    (!isDirectnNavigation || (invoiceListInfoResult?.isPresent && !invoiceListInfoResultInProgress))
    && (invoiceNumbers.length - currentInvoiceIndex < 5 ||
      currentInvoiceIndex === -1);

  useEffect(() => {
    if (isFetchMore) {
      fetchMore();
    }
  }, [
    fetchMore,
    canFetchMore,
    isFetchingMore,
    invoiceNumbers.length,
    currentInvoiceIndex,
  ]);

  if (currentInvoiceIndex === -1 || invoiceNumbers.length < 2) {
    return null;
  }

  return (
    <div className={styles.invoiceNav}>
      <IconButton
        id="previous-invoice-link-button"
        disabled={currentInvoiceIndex === 0}
        className={styles.invoiceNavButton}
        aria-label={t("invoices.navigation.previous")}
        onClick={() => {
          history.push(getInvoiceUrl(currentInvoiceIndex - 1));
        }}
      >
        <NavArrowIcon fillColor={getColor('--primary-color')} />
      </IconButton>
      <span>{`${currentInvoiceIndex + 1} of ${total}`}</span>
      <IconButton
        id="next-invoice-link-button"
        className={styles.invoiceNavButton}
        aria-label={t("invoices.navigation.next")}
        disabled={currentInvoiceIndex === invoiceNumbers.length - 1}
        onClick={() => {
          history.push(getInvoiceUrl(currentInvoiceIndex + 1));
        }}
      >
        <NavArrowIcon 
          isRight 
          fillColor={getColor('--primary-color')}
      />
      </IconButton>
    </div>
  );
};

export default InvoiceNavigation;
